import {
  CheckOutlined,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { message, Popconfirm, Tag, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { newsApi } from "api/news.api";

import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  INA,
  TableContent,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { areaStore } from "store/areaStore";
import { ModalStatus } from "types/modal";
import { News } from "types/news";
import { QueryParam } from "types/query";
import { getTitle } from "utils";
import { formatDate } from "utils/date";
import { $url } from "utils/url";
import { NewsModal } from "./components/NewsModal";
import { VoteModal } from "./components/VoteModal";

interface NewsQuery extends QueryParam {
  areaId: number | null;
}

export const NewsPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [areaId, setAreaId] = useState<string | number>("");
  const [query, setQuery] = useState<NewsQuery>({
    page: 1,
    limit: 10,
    search: "",
    areaId: null,
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<News[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState<Partial<News>>({});
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");
  const [visibleVoteModal, setVisibleVoteModal] = useState(false);

  const debounceSearch = useCallback(
    debounce((keyword: string) => setQuery({ ...query, search: keyword, page: 1, limit: 20 }), 300),
    [setQuery, query],
  );

  useEffect(() => {
    document.title = getTitle(title);
    areaStore.fetchList();
  }, []);

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    setLoading(true);
    const res = await newsApi.findAll(query);
    setLoading(false);
    setData(res.data.newses);
    setTotal(res.data.total);
  };

  const handleDelete = async (news: News) => {
    setLoading(true);
    try {
      await newsApi.delete(news.id);
      fetchData();
      message.info("Xóa thành công");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (news: News) => {
    setLoading(true);
    try {
      if (news.isVisible) {
        await newsApi.update(news.id, {
          news: {
            isVisible: false,
          },
        });
        message.info("Ẩn thành công!");
      } else {
        await newsApi.update(news.id, {
          news: {
            isVisible: true,
          },
        });
        message.info("Hiển thị thành công!");
      }
    } finally {
      fetchData();
      setLoading(false);
    }
  };

  const columns = useMemo<TableColumnsType<News>>(
    () => [
      {
        title: "STT",
        key: "stt",
        width: 80,
        render: (_, __, index) => <span>{(query.page - 1) * query.limit + index + 1}</span>,
      },
      {
        title: "Tiêu đề",
        dataIndex: "title",
        key: "title",
        width: 280,
        render: (_, record) => (
          <INA
            image={record.thumbnail ? $url(record.thumbnail) : undefined}
            name={record.title}
            additional={record.newsCategory?.name || "--"}
          />
        ),
      },
      {
        title: "Nội dung",
        dataIndex: "shortDescription",
        key: "shortDescription",
        render: (_, record) => (
          <div className="space-y-1 min-w-0">
            <TableContent>{record.shortDescription || "--"}</TableContent>
            {record.vote ? (
              <button
                type="button"
                className="text-blue tooltip-text underline decoration-dotted"
                onClick={() => {
                  setSelectedNews(record);
                  setVisibleVoteModal(true);
                }}
              >
                <b>Bình chọn:</b> {record.vote.name}
              </button>
            ) : null}
          </div>
        ),
      },
      {
        title: "Trạng thái",
        dataIndex: "isVisible",
        key: "isVisible",
        width: 120,
        render: (value) => <Tag color={value ? "green" : "red"}>{value ? "Hiển thị" : "Bị ẩn"}</Tag>,
      },
      {
        title: "Ngày tạo",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 120,
        render: (value) => <span>{formatDate(value)}</span>,
      },
      {
        title: "Thao tác",
        key: "action",
        width: 160,
        align: "center",
        fixed: "right",
        render: (_, record) => (
          <div className="flex gap-2 justify-center items-center">
            <Tooltip title="Cập nhật">
              <ActionButton
                aria-label="Cập nhật"
                onClick={() => {
                  setSelectedNews(record);
                  setVisibleModal(true);
                  setModalStatus("update");
                }}
              >
                <EditOutlined style={{ color: "#6c84e5" }} />
              </ActionButton>
            </Tooltip>
            <Tooltip title={record.isVisible ? "Ẩn" : "Hiển thị"}>
              <Popconfirm
                placement="topLeft"
                title={`Xác nhận ${record.isVisible ? "ẩn" : "hiển thị"} tin tức này?`}
                onConfirm={() => handleToggle(record)}
                okText="Ok"
                cancelText="Không"
              >
                <ActionButton aria-label={record.isVisible ? "Ẩn" : "Hiển thị"}>
                  {record.isVisible ? (
                    <EyeInvisibleOutlined style={{ color: "#f59e0b" }} />
                  ) : (
                    <CheckOutlined style={{ color: "#22c55e" }} />
                  )}
                </ActionButton>
              </Popconfirm>
            </Tooltip>
            <Tooltip title="Xóa">
              <Popconfirm
                placement="top"
                title="Xác nhận xóa tin tức này?"
                onConfirm={() => handleDelete(record)}
                okText="Xóa"
                cancelText="Không"
              >
                <ActionButton aria-label="Xóa">
                  <DeleteOutlined style={{ color: "#ef4444" }} />
                </ActionButton>
              </Popconfirm>
            </Tooltip>
          </div>
        ),
      },
    ],
    [query.limit, query.page],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  const areaOptions = areaStore.list.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              placeholder: "Tìm kiếm",
              onChange: (value) => {
                setSearch(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearch("");
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            filters={[
              {
                key: "areaId",
                value: areaId,
                placeholder: "Khu vực",
                width: 200,
                options: areaOptions,
                allowClear: true,
                showSearch: true,
                onChange: (value) => {
                  setAreaId(value ?? "");
                  setQuery((prev) => ({
                    ...prev,
                    page: 1,
                    areaId: value ? Number(value) : null,
                  }));
                },
              },
            ]}
            leftActions={
              <AppButton
                onClick={() => {
                  setVisibleModal(true);
                  setModalStatus("create");
                  setSelectedNews({});
                }}
                tone="primary"
                icon={<PlusOutlined />}
              >
                Tạo mới tin tức
              </AppButton>
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<News>
            loading={loading}
            pagination={false}
            rowKey="id"
            dataSource={data}
            columns={columns}
            scroll={{ x: 1100 }}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              query.page = page;
              query.limit = limit;
              setQuery({ ...query });
            }}
          />
        }
      />

      <NewsModal
        onSubmitOk={fetchData}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        news={selectedNews}
        status={modalStatus}
      />

      <VoteModal
        visible={visibleVoteModal}
        onClose={() => setVisibleVoteModal(false)}
        selectData={selectedNews}
      />
    </div>
  );
});
